/**
 * "LEVEL 7" — the level-up banner.
 *
 * The renderer for {@link levelPlan}. Built as a deliberate sibling of
 * `StreakBadge`: same typeface, same three-phase shape, same band across the
 * foot of the table, same rule that a new one replaces whatever is on screen
 * rather than stacking. What differs is the key — azure and cool where the
 * streak is gold and hot — and that it never claims to be worth points.
 *
 * The letters arrive one at a time rather than the block arriving whole. That
 * is the "slam": at 18 ms apart a six-letter banner is fully in within 110 ms,
 * which reads as a single impact with texture rather than as six events.
 *
 * ## What this does NOT do
 *
 * It does not move the scene. No shake, no zoom, no dolly, no tilt of the
 * table, and no whiteout — those were all removed from this game once already
 * because they blur the cards, and a cosmetic milestone is the last thing worth
 * bringing them back for. Everything here is additive light in a band that the
 * play area does not occupy.
 *
 * Lives on the **overlay** layer, parallax ratio 0.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { gsap } from 'gsap';

import { LEVEL_COLOUR, LEVEL_COLOUR_BRIGHT, levelPlan } from '@/vfx/levelCeremony';
import { palette, type as typeTokens } from '@/core/tokens';
import { CARD, DESIGN, playBottom } from '@/pixi/layout';

/** Design units. Smaller than the streak numeral: it is worth less. */
const SIZE = 62;
const TRACK = 0.1;

/** The band behind the letters, as a fraction of the design width. */
const BAND_WIDTH = 0.74;
const BAND_HEIGHT = SIZE * 1.6;

export interface LevelBannerOptions {
  reducedMotion?: boolean;
}

export class LevelBanner extends Container {
  readonly #band: Graphics;
  readonly #group: Container;
  readonly #letters: Text[] = [];

  #timeline: gsap.core.Timeline | null = null;
  #reduced: boolean;

  constructor(options: LevelBannerOptions = {}) {
    super({ label: 'level-banner' });
    this.eventMode = 'none';
    this.#reduced = options.reducedMotion ?? false;

    // Additive, so the band lifts the felt toward azure rather than laying a
    // panel over it. A level-up should look like the room being lit, not like
    // a card being dealt onto the table.
    this.#band = new Graphics({ blendMode: 'add', alpha: 0 });
    this.addChild(this.#band);

    this.#group = new Container({ label: 'level-banner-type' });
    this.addChild(this.#group);

    this.#paintBand();
    this.position.set(DESIGN.width * 0.5, this.#homeY());
    this.visible = false;
  }

  setReducedMotion(reduced: boolean): void {
    this.#reduced = reduced;
  }

  /**
   * Announce a level.
   *
   * @param level - the level reached.
   * @param delay - seconds to wait. Non-zero when a streak callout is already
   *   on screen from the same move; see `QUEUE_GAP`.
   */
  show(level: number, delay = 0): void {
    const plan = levelPlan(level);

    this.#timeline?.kill();
    this.#setText(`LEVEL ${plan.level}`);
    this.position.set(DESIGN.width * 0.5, this.#homeY());
    this.visible = true;

    if (this.#reduced) {
      // Still announced, just not animated: the number is the information and
      // withholding it would be the wrong accommodation.
      this.#group.scale.set(plan.scale);
      this.#group.alpha = 1;
      this.#band.alpha = 0.3;
      for (const letter of this.#letters) {
        letter.alpha = 1;
        letter.y = 0;
      }
      this.#timeline = gsap
        .timeline({ delay })
        .to({}, { duration: plan.hold })
        .to([this.#group, this.#band], {
          alpha: 0,
          duration: 0.2,
          onComplete: () => {
            this.visible = false;
          },
        });
      return;
    }

    const tl = gsap.timeline({
      delay,
      onComplete: () => {
        this.visible = false;
        this.#timeline = null;
      },
    });

    // The band, and the type's overall scale. A plain level lands exactly on
    // its final size — `overshoot` is 1 — so only a milestone punches past it.
    tl.fromTo(
      this.#band,
      { alpha: 0 },
      { alpha: 0.34, duration: plan.arrive * 0.6, ease: 'power2.out' },
      0,
    );
    tl.fromTo(
      this.#group.scale,
      { x: plan.scale * 0.94, y: plan.scale * 0.94 },
      {
        x: plan.scale * plan.overshoot,
        y: plan.scale * plan.overshoot,
        duration: plan.arrive * 0.55,
        ease: 'power3.out',
      },
      0,
    );
    if (plan.overshoot !== 1) {
      tl.to(
        this.#group.scale,
        { x: plan.scale, y: plan.scale, duration: 0.12, ease: 'power2.inOut' },
        plan.arrive * 0.55,
      );
    }

    // The slam: each letter drops the last few units into place, staggered.
    this.#letters.forEach((letter, i) => {
      tl.fromTo(
        letter,
        { alpha: 0, y: -SIZE * 0.32 },
        { alpha: 1, y: 0, duration: 0.16, ease: 'power4.out' },
        i * plan.letterStagger,
      );
    });

    const settled = plan.arrive + plan.hold;
    tl.to(
      [this.#group, this.#band],
      { alpha: 0, duration: plan.exit, ease: 'power2.in' },
      settled,
    );
    // Reset for the next use — `alpha` is tweened on the group, so the letters'
    // own alphas have to come back up before the next `fromTo` reads them.
    tl.set(this.#group, { alpha: 1 });

    this.#timeline = tl;
  }

  /** Stop and hide, for a round ending under a banner. */
  clear(): void {
    this.#timeline?.kill();
    this.#timeline = null;
    this.visible = false;
    this.#group.alpha = 1;
    this.#band.alpha = 0;
  }

  /**
   * Rebuild the letters.
   *
   * One `Text` per character so they can be staggered. Rasterised white and
   * tinted, the same trick `StreakBadge` uses: a `fill` change re-rasterises the
   * glyph into a new texture, and this runs nine or ten times a round.
   */
  #setText(text: string): void {
    for (const letter of this.#letters) letter.destroy();
    this.#letters.length = 0;
    this.#group.removeChildren();

    const style = new TextStyle({
      fontFamily: typeTokens.display,
      fontSize: SIZE,
      letterSpacing: SIZE * TRACK,
      fill: palette.white,
    });

    const glyphs = [...text];
    const widths: number[] = [];
    let total = 0;
    for (const ch of glyphs) {
      const letter = new Text({ text: ch, style });
      letter.anchor.set(0.5);
      // The brighter end of the gradient on the number, the deeper end on the
      // word — so "LEVEL" reads as the label and the digits as the value,
      // without a second type size.
      letter.tint = /[0-9]/.test(ch) ? LEVEL_COLOUR_BRIGHT : LEVEL_COLOUR;
      this.#letters.push(letter);
      this.#group.addChild(letter);
      const w = letter.width || SIZE * 0.5;
      widths.push(w);
      total += w;
    }

    let x = -total * 0.5;
    glyphs.forEach((_, i) => {
      const w = widths[i] ?? 0;
      const letter = this.#letters[i];
      if (letter) letter.x = x + w * 0.5;
      x += w;
    });
  }

  #paintBand(): void {
    const w = DESIGN.width * BAND_WIDTH;
    this.#band.clear();
    this.#band
      .roundRect(-w * 0.5, -BAND_HEIGHT * 0.5, w, BAND_HEIGHT, BAND_HEIGHT * 0.5)
      .fill({ color: LEVEL_COLOUR, alpha: 0.5 });
  }

  /**
   * The same band the streak callout uses: below the play, above the bottom.
   *
   * Sharing the rule rather than the number, because the two never appear at
   * the same instant — the level waits for the streak — and a milestone that
   * appeared somewhere else would read as a different system.
   */
  #homeY(): number {
    const half = BAND_HEIGHT * 0.5;
    const wanted = playBottom() + CARD.height * 0.1 + half;
    return Math.min(wanted, DESIGN.height * 0.92 - half);
  }
}
